import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import styles from './Activities.module.scss';

interface BookingActivity {
  branchId: string;
  branchName: string;
  branchLogo?: string;
  checkoutComments?: string;
  paymentAmount?: number;
  status: string;
  date?: string;
  startTime?: string;
  endTime?: string;
}

interface ActivitiesState {
  activeTab: 'booking' | 'tournament' | 'social';
  bookingActivities: BookingActivity[];
  isLoading: boolean;
  error: string | null;
}

function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    return <WrappedComponent {...props} navigate={navigate} />;
  };
}

class Activities extends Component<any, ActivitiesState> {
  constructor(props: any) {
    super(props);
    this.state = {
      activeTab: 'booking',
      bookingActivities: [],
      isLoading: true,
      error: null
    };
  }

  componentDidMount() {
    this.loadBookingActivities();
  }

  loadBookingActivities = async () => {
    this.setState({ isLoading: true, error: null });
    try {
      const response = await apiService.getCustomerActivities();
      if (response && response.success && response.data) {
        this.setState({
          bookingActivities: response.data.activities || [],
          isLoading: false
        });
      } else {
        throw new Error(response?.message || 'Failed to load activities');
      }
    } catch (error: any) {
      console.error('[Activities] Failed to load booking activities:', error);
      this.setState({
        isLoading: false,
        error: error?.message || 'Failed to load activities. Please try again.'
      });
    }
  };

  handleTabChange = (tab: 'booking' | 'tournament' | 'social') => {
    this.setState({ activeTab: tab });
  };

  getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return styles.statusCompleted;
      case 'cancelled':
        return styles.statusCancelled;
      case 'confirmed':
        return styles.statusConfirmed;
      default:
        return styles.statusDefault;
    }
  };

  formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  formatTime = (timeString?: string) => {
    if (!timeString) return 'N/A';
    try {
      // Handle both "HH:MM:SS" and "HH:MM" formats
      const timeParts = timeString.split(':');
      const hours = parseInt(timeParts[0]);
      const minutes = timeParts[1];
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
      return `${displayHours.toString().padStart(2, '0')}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  render() {
    const { activeTab, bookingActivities, isLoading, error } = this.state;

    return (
      <>
        <DashboardHeader />
        <div className={styles.activitiesContainer}>
          <div className={styles.activitiesContent}>
            {/* Header Section */}
            <div className={styles.headerSection}>
              <h1 className={styles.pageTitle}>My Activities</h1>
              <p className={styles.pageSubtitle}>
                View all your booking and activity history
              </p>
            </div>

            {/* Tabs */}
            <div className={styles.tabsContainer}>
              <button
                className={`${styles.tab} ${activeTab === 'booking' ? styles.tabActive : ''}`}
                onClick={() => this.handleTabChange('booking')}
              >
                Booking
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'tournament' ? styles.tabActive : ''}`}
                onClick={() => this.handleTabChange('tournament')}
              >
                Tournament
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'social' ? styles.tabActive : ''}`}
                onClick={() => this.handleTabChange('social')}
              >
                Social
              </button>
            </div>

            {/* Tab Content */}
            <div className={styles.tabContent}>
              {activeTab === 'booking' && (
                <div className={styles.bookingTabContent}>
                  {isLoading ? (
                    <div className={styles.loadingContainer}>
                      <div className={styles.loadingSpinner}></div>
                      <p>Loading activities...</p>
                    </div>
                  ) : error ? (
                    <div className={styles.errorContainer}>
                      <div className={styles.errorIcon}>⚠️</div>
                      <h3>Error Loading Activities</h3>
                      <p>{error}</p>
                      <button
                        className={styles.retryButton}
                        onClick={this.loadBookingActivities}
                      >
                        Retry
                      </button>
                    </div>
                  ) : bookingActivities.length === 0 ? (
                    <div className={styles.emptyState}>
                      <div className={styles.emptyStateIcon}>📋</div>
                      <h3>No Booking Activities</h3>
                      <p>You haven't completed any bookings yet.</p>
                      <button
                        className={styles.emptyStateButton}
                        onClick={() => this.props.navigate('/customer/organizers')}
                      >
                        Browse Organizers
                      </button>
                    </div>
                  ) : (
                    <div className={styles.activitiesList}>
                      {bookingActivities.map((activity, index) => (
                        <div key={index} className={styles.activityCard}>
                          <div className={styles.activityHeader}>
                            <div className={styles.branchInfo}>
                              {activity.branchLogo && (
                                <img
                                  src={activity.branchLogo}
                                  alt={activity.branchName}
                                  className={styles.branchLogo}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                              <div className={styles.branchDetails}>
                                <h3 className={styles.branchName}>{activity.branchName}</h3>
                                <span className={`${styles.statusBadge} ${this.getStatusBadgeClass(activity.status)}`}>
                                  {activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className={styles.activityDetails}>
                            {activity.date && (
                              <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Date:</span>
                                <span className={styles.detailValue}>{this.formatDate(activity.date)}</span>
                              </div>
                            )}
                            {(activity.startTime || activity.endTime) && (
                              <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Time:</span>
                                <span className={styles.detailValue}>
                                  {activity.startTime && activity.endTime
                                    ? `${this.formatTime(activity.startTime)} - ${this.formatTime(activity.endTime)}`
                                    : activity.startTime
                                    ? this.formatTime(activity.startTime)
                                    : this.formatTime(activity.endTime)}
                                </span>
                              </div>
                            )}
                            {activity.paymentAmount !== undefined && activity.paymentAmount !== null && (
                              <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Payment Amount:</span>
                                <span className={styles.detailValue}>{this.formatCurrency(activity.paymentAmount)}</span>
                              </div>
                            )}
                            {activity.checkoutComments && (
                              <div className={styles.commentsSection}>
                                <strong>Checkout Comments:</strong>
                                <p>{activity.checkoutComments}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'tournament' && (
                <div className={styles.tabPlaceholder}>
                  <div className={styles.placeholderIcon}>🏆</div>
                  <h3>Tournament Activities</h3>
                  <p>Tournament activity history will be displayed here.</p>
                </div>
              )}

              {activeTab === 'social' && (
                <div className={styles.tabPlaceholder}>
                  <div className={styles.placeholderIcon}>📱</div>
                  <h3>Social Activities</h3>
                  <p>Social activity history will be displayed here.</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
}

export default withNavigation(Activities);
